import { Box, Button } from "@mui/material";

interface CardPaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export default function CardPagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
}: CardPaginationProps) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const maxVisiblePages = 5; // Show 5 pages before ellipsis

  const generatePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // If current page is in first 5 pages, show 1-5 ... last
      if (currentPage <= maxVisiblePages) {
        for (let i = 1; i <= maxVisiblePages; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
      // If current page is in last 5 pages, show first ... last 5
      else if (currentPage > totalPages - maxVisiblePages) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - maxVisiblePages + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      }
      // If current page is in middle, show first ... current-1, current, current+1 ... last
      else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pages = generatePages();

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      gap={1}
      mt={3}
    >
      {/* Previous Button */}
      <Button
        variant="outlined"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        sx={{
          minWidth: "40px",
          height: "40px",
          padding: 0,
          borderRadius: 1,
          border: (theme) =>
            `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
        }}
      >
        &lt;
      </Button>

      {/* Page Numbers */}
      {pages.map((page, index) => {
        if (page === "...") {
          return (
            <Box
              key={`ellipsis-${index}`}
              sx={{
                minWidth: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              ...
            </Box>
          );
        }

        return (
          <Button
            key={page}
            variant={currentPage === page ? "contained" : "outlined"}
            onClick={() => onPageChange(page as number)}
            sx={{
              minWidth: "40px",
              height: "40px",
              padding: 0,
              borderRadius: 1,
              border: (theme) =>
                currentPage === page
                  ? "none"
                  : `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
              ...(currentPage === page && {
                backgroundColor: (theme) => theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.primary.dark,
                },
              }),
            }}
          >
            {page}
          </Button>
        );
      })}

      {/* Next Button */}
      <Button
        variant="outlined"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        sx={{
          minWidth: "40px",
          height: "40px",
          padding: 0,
          borderRadius: 1,
          border: (theme) =>
            `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
        }}
      >
        &gt;
      </Button>
    </Box>
  );
}
